<template>
    <router-view></router-view>
</template>

<script>
    import Cookies from 'js-cookie';
    import * as cookies from './config/cookies';
    import { mapState } from 'vuex';
    import moment from 'moment';
    import NetworkService from './services/NetworkService';
    import router from './router';

    const APP_NAME = 'Clever Start';

    export default {
        beforeCreate() {
            if (this.$offline) {
                this.$router.push({ name: 'trainer:games' });
            } else {
                const token = Cookies.get(cookies.USER_TOKEN);
                token && this.$store.dispatch('setUserToken', token);
                NetworkService.handleNetworkConnection(
                    () => this.$toasted.error(this.$i18n.t('common.common_network_offline')),
                    () => {
                        this.$toasted.success(this.$i18n.t('common.common_network_online'));
                        router.go();
                    }
                );
            }
        },
        watch: {
            '$store.state.auth.user': {
                handler(user) {
                    if (user) {
                        const { type: userType = '', pointsAccesses = [] } = user;
                        const amountOfAccesses = pointsAccesses.length;
                        const isCourseSelected = !!this.$store.state?.trainer?.course;
                        if ((userType === 'student' || userType === 'olympiad-student') && !isCourseSelected) {
                            if (amountOfAccesses > 1) {
                                this.$router.push({ name: 'select-course' });
                            } else {
                                this.$store.dispatch('setCourse', this.user.pointsAccesses[0]);
                                this.$route.name === 'login' && this.$router.push({ name: 'dashboard' });
                            }
                        } else {
                            this.$route.name === 'login' && this.$router.push({ name: 'dashboard' });
                        }
                    } else {
                        this.$route.name !== 'login' && this.$router.push({ name: 'login' });
                    }
                },
                immediate: false,
            },
            $route: {
                handler(to, from) {
                    this.$store.dispatch('setPreviousRoute', from);
                    if (
                        this.$store.state.trainer.paramsArray &&
                        to.meta.type !== 'trainer' &&
                        to.name !== 'create:game'
                    ) {
                        this.$store.dispatch('setParamsArray', null);
                    }
                },
                immediate: true,
            },
            user: {
                handler(user) {
                    if (user) {
                        this.$i18n.locale = user.locale;
                        this.$validator.localize(this.$i18n.locale);
                        if (user.type === 'student') {
                            this.$echo
                                .private(`stage.${user.id}`)
                                .listen('PrepareStage', data => {
                                    let stage = data.stage;
                                    if (
                                        this.$route.name !== 'olympiads:start' &&
                                        this.$route.name !== 'olympiads:show'
                                    ) {
                                        if (stage.position === 0) {
                                            this.$toasted.info(
                                                this.$i18n.t('olympiads.olympiads_layout_olympiad') +
                                                    stage.level.olympiad.name +
                                                    this.$i18n.t('olympiads.olympiads_notification_willStartSoon')
                                            );
                                            this.$router.push({
                                                name: 'olympiads:show',
                                                params: { id: stage.level.olympiad.id },
                                            });
                                        } else {
                                            this.$router.push({
                                                name: 'olympiads:start',
                                                params: { id: stage.level.id },
                                            });
                                        }
                                    }
                                })
                                .listen('StartStage', data => {
                                    let stage = data.stage;
                                    if (
                                        this.$route.name !== 'olympiads:start' &&
                                        this.$route.name !== 'olympiads:show'
                                    ) {
                                        this.$router.push({ name: 'olympiads:start', params: { id: stage.level.id } });
                                    }
                                })
                                .listen('FinishStage', data => {
                                    if (this.$route.name !== 'olympiads:start') {
                                        setTimeout(() => {
                                            this.$router.push({
                                                name: 'olympiads:stage-results',
                                                params: { id: data.stage.id },
                                            });
                                        }, 1000);
                                    }
                                });
                            this.$echo.private(`battle-invite.${user.id}`).listen('BattleInvite', data => {
                                this.$store.dispatch('setBattleInvitation', data);
                            });
                            this.$echo.private(`cancel-battle-invite.${user.id}`).listen('BattleCancelInvite', data => {
                                if (data.status === 'invitation_accepted') {
                                    this.$router.push({ name: 'battle:bad-connection' });
                                    this.$store.dispatch('setBattleDeclined', true);
                                    this.$store.dispatch('setBattleInvitation', null);
                                    return;
                                }
                                if (data.declineFrom === 'sender') {
                                    this.$store.dispatch('setBattleInvitation', null);
                                } else {
                                    this.$store.dispatch('setBattleDeclined', true);
                                }
                            });
                            this.$echo
                                .private(`accepted-battle-invite.${user.id}`)
                                .listen('BattleAcceptedInvite', () => {
                                    this.$store.dispatch('setBattleAccepted', true);
                                });
                            this.$echo.private(`battle-state.${user.id}`).listen('BattleStateChangeEvent', data => {
                                this.$store.dispatch('setBattlePaused', data.isPaused);
                            });
                            this.$echo
                                .private(`battle-force-completed.${this.user.id}`)
                                .listen('BattleForceCompletedEvent', data => {
                                    let isVictory = data.winnerId === this.user.id;
                                    this.$router.push({
                                        name: 'battle:result',
                                        params: {
                                            isVictory: isVictory,
                                            isTrainingBattle: data.isTrainingBattle,
                                            isForceCompleted: true,
                                        },
                                    });
                                    this.$store.dispatch('setBattlePaused', null);
                                });
                        }
                    } else {
                        const availableLanguages = Object.keys(this.$i18n.messages);
                        this.$i18n.locale = availableLanguages.find(lang => navigator.language.includes(lang)) ?? 'ru';
                        this.$validator.localize(this.$i18n.locale);
                    }
                    moment.locale(this.$i18n.locale);
                },
                immediate: true,
            },
            '$route.meta.title': {
                handler(val) {
                    if (val) {
                        let title = val;
                        if (typeof title === 'function') {
                            title = title(this.$route);
                            if (title instanceof Promise) {
                                title
                                    .then(response => {
                                        document.title = APP_NAME + ' - ' + response;
                                    })
                                    .catch(() => {
                                        this.$toasted.error(this.$root.$t('errors.getting_data'));
                                    });
                                return;
                            }
                        }
                        document.title = APP_NAME + ' - ' + this.$i18n.t(title);
                    }
                },
                immediate: true,
            },
        },
        computed: {
            ...mapState({
                user: state => state.auth.user,
            }),
        },
    };
</script>
